import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useMemo, useState} from 'react'
import {getEntityCompositeID, sortFindingsBySeverity} from './entity-helpers'
import type {FindingEntity} from './types'

const ignoreFindingIds = async (basePath: string, compositeIds: string[]): Promise<Response> => {
  const response = await verifiedFetchJSON(`${basePath}/code_review_findings/ignore`, {
    method: 'POST',
    body: {entity_ids: compositeIds},
  })

  if (!response.ok) {
    throw new Error(`Failed to ignore findings (status ${response.status} ${response.statusText})`)
  }

  return response
}

const unignoreFindingIds = async (basePath: string, compositeIds: string[]): Promise<Response> => {
  const response = await verifiedFetchJSON(`${basePath}/code_review_findings/ignore`, {
    method: 'DELETE',
    body: {entity_ids: compositeIds},
  })

  if (!response.ok) {
    throw new Error(`Failed to unignore findings (status ${response.status} ${response.statusText})`)
  }

  return response
}

export function useIgnoreFindings(findings: FindingEntity[], pullRequestBasePath: string, ignoredEntityIds: string[]) {
  const [ignoredIds, setIgnoredIds] = useState<Set<string>>(() => new Set(ignoredEntityIds))

  const handleDismiss = useCallback(
    async (id: string) => {
      setIgnoredIds(prev => new Set(prev).add(id))
      try {
        await ignoreFindingIds(pullRequestBasePath, [id])
      } catch {
        setIgnoredIds(prev => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })
      }
    },
    [pullRequestBasePath],
  )

  const handleReopen = useCallback(
    async (id: string) => {
      setIgnoredIds(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      try {
        await unignoreFindingIds(pullRequestBasePath, [id])
      } catch {
        setIgnoredIds(prev => new Set(prev).add(id))
      }
    },
    [pullRequestBasePath],
  )

  const handleDismissAll = useCallback(async () => {
    const ids = findings.map(finding => getEntityCompositeID(finding))
    try {
      await ignoreFindingIds(pullRequestBasePath, ids)
      setIgnoredIds(new Set(ids))
    } catch {
      // TODO: communicate error to user
    }
  }, [findings, pullRequestBasePath])

  const handleReopenAll = useCallback(async () => {
    const ids = findings.map(finding => getEntityCompositeID(finding))
    try {
      await unignoreFindingIds(pullRequestBasePath, ids)
      setIgnoredIds(new Set())
    } catch {
      // TODO: communicate error to user
    }
  }, [findings, pullRequestBasePath])

  const openFindings = useMemo(
    () => sortFindingsBySeverity(findings.filter(f => !ignoredIds.has(getEntityCompositeID(f)))),
    [findings, ignoredIds],
  )
  const ignoredFindings = useMemo(
    () => sortFindingsBySeverity(findings.filter(f => ignoredIds.has(getEntityCompositeID(f)))),
    [findings, ignoredIds],
  )

  return {openFindings, ignoredFindings, handleDismiss, handleReopen, handleDismissAll, handleReopenAll}
}
